/**
 * ItemDetail component
 * @returns <ItemDetail />
 */

import Badge from "./Badge";
import Button from "./Button";
import BoxWithBorder from "./BoxWithBorder";
import Discount from "./Discount";
import DiscountDetail from "./DiscountDetail";
import Select from "./Select";
import InputContainer from "./InputContainer";
import InputCheckBox from "./InputCheckBox";
import { Counter } from "./Counter";
import Description from "./Description";
import LegalInformation from "./LegalInformation";
import ProductImagesSlider from "./Carousel";
import Toast from "./Toast";

import { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { useDispatch, useSelector } from "react-redux";
import { useAddProductMutation } from "../redux/apiSlice";
import { removeAllowedMaintenances } from "../redux/cartSlice";
import { formatPrice } from "../scripts/helpers/formatPrice";
import { toastConfig } from "../mock/data";
import { useGetCartQuery } from "../redux/apiSlice";

const ItemDetailAccesorio = ({ item }) => {
    const dispatch = useDispatch();
    const allowedMaintenances = useSelector(
        (state) => state.cart.allowedMaintenances
    );

    // Get the cart items because I need the getCart endpoint to be fullfiled
    const { data: cart } = useGetCartQuery();

    const {
        title,
        detailSubtitle: subtitle,
        images, // array of strings
        description, // object
        legalInfo, // array of strings
        installationCost,
        currency, // string
        discount,
        type,
        id,
        versions,
        price,
        discount_amount,
    } = item;

    const hasDiscountedPrice =
        discount_amount !== null &&
        discount_amount !== undefined &&
        discount_amount !== "";
    const effectivePrice =
        hasDiscountedPrice
            ? Number(discount_amount)
            : Number(price);

    // Item State
    const initialState = {
        id,
        type,
        title,
        images,
        description,
        installationCost,
        price: null,
        quantity: 1,
        currency,
        selectedVersion: null,
    };

    // let itemAdded;

    const [addProduct] = useAddProductMutation();
    const [itemToAddToCart, setItemAddToCart] = useState(initialState);
    const [addInstallationCost, setAddInstallationCost] = useState(false);
    const [totalPrice, setTotalPrice] = useState(null);

    const onAddHandler = () => {
        if (!cart) {
            return;
        }

        setItemAddToCart((prevState) => {
            const newQuantity = prevState.quantity + 1;

            return {
                ...prevState,
                quantity: newQuantity,
            };
        });
    };

    const onDecreaseHandler = () => {
        if (!cart) {
            return;
        }

        if (itemToAddToCart.quantity > 1) {
            setItemAddToCart((prevState) => {
                const newQuantity = prevState.quantity - 1;

                return {
                    ...prevState,
                    quantity: newQuantity,
                };
            });
        }
    };

    const selectChangeHandler = (value) => {
        if (!cart) {
            return;
        }

        setItemAddToCart((prevState) => {
            const updatedItem = {
                ...prevState,
                selectedVersion: value,
                price: effectivePrice,
            };

            return updatedItem;
        });
    };

    const installationChangeHandler = () => {
        if (!cart) {
            return;
        }

        setAddInstallationCost(!addInstallationCost);

        setItemAddToCart((prevState) => {
            const updatedItem = {
                ...prevState,
                installationCost: installationCost,
            };

            return updatedItem;
        });
    };

    const addToCartHandler = () => {
        if (!cart) {
            return;
        }

        if (!allowedMaintenances || allowedMaintenances === null) {
            toast(
                <Toast
                    type="add-product"
                    title={"Agregaste al carrito"}
                    product={itemToAddToCart}
                    totalPrice={formatPrice(effectivePrice)}
                />,
                toastConfig
            );
            addProduct({
                product_type: addInstallationCost
                    ? "accessory_variation"
                    : "accessory",
                product_id: itemToAddToCart.id,
                quantity: itemToAddToCart.quantity,
                extra_info: itemToAddToCart.selectedVersion,
            });

            dispatch(removeAllowedMaintenances());
        } else {
            toast(
                <Toast
                    type="add-product"
                    title={"Error al agregar al carrito"}
                    error={
                        "Tienes una Mantención en el carrito. Solo puedes agregar Mantenciones."
                    }
                />,
                toastConfig
            );
        }
    };

    useEffect(() => {
        let totalPrice =
            (Number(itemToAddToCart.price) +
                (addInstallationCost
                    ? Number(itemToAddToCart.installationCost)
                    : 0)) *
            itemToAddToCart.quantity;

        if (totalPrice === 0 || totalPrice === null) {
            setTotalPrice(() => "");
        } else {
            setTotalPrice(() => totalPrice);
        }
    }, [itemToAddToCart, addInstallationCost]);

    return (
        <section className="item-detail">
            <div className="wrapper">
                <div className="item-detail__grid">
                    <div className="box-left">
                        <div className="title-container mobile-only">
                            {/* Out of stock */}
                            {item.stock === 0 && (
                                <Badge
                                    type="badge-detail"
                                    text="Agotado"
                                    backgroundColor="#454545"
                                />
                            )}
                            {/* Low stock */}
                            {item.stock === 1 && (
                                <Badge
                                    type="badge-detail"
                                    text="Último disponible"
                                    backgroundColor="#AD0B00"
                                />
                            )}

                            {/* Main title in desktop */}
                            <h1 className="title">{title}</h1>

                            {/* OPTIONAL: Subtitle */}
                            {subtitle && <p className="subtitle">{subtitle}</p>}
                        </div>

                        {/* Main image or carousel*/}
                        {images.length > 1 && (
                            <ProductImagesSlider images={images} />
                        )}
                        {images.length === 1 && (
                            <img
                                className="item-detail__image"
                                src={images[0].url}
                                alt=""
                            />
                        )}

                        {/* OPTIONAL: Description */}
                        {description && <Description {...description} />}

                        {/* OPTIONAL: Secondary image */}
                        {/* {secondaryImage && <img className="item-detail__image" src={image} alt="" />} */}
                    </div>

                    <div className={"box-right --mantencion"}>
                        <div className="title-container desktop-only">
                            {/* Out of stock */}
                            {item.stock === 0 && (
                                <Badge
                                    type="badge-detail"
                                    text="Agotado"
                                    backgroundColor="#454545"
                                />
                            )}
                            {/* Low stock */}
                            {item.stock === 1 && (
                                <Badge
                                    type="badge-detail"
                                    text="Último disponible"
                                    backgroundColor="#AD0B00"
                                />
                            )}

                            {/* Main title in desktop */}
                            <h1 className="title">{title}</h1>

                            {/* OPTIONAL: Subtitle */}
                            {subtitle && <p className="subtitle">{subtitle}</p>}
                        </div>

                        {/* Seleccionar version */}
                        <InputContainer
                            title={"Selecciona una versión de tu Lexus"}
                            subtitle={""}
                            extraClass={""}
                        >
                            <Select
                                type={"select"}
                                name={"seleccionar-version"}
                                placeholder={
                                    "Selecciona la versión de tu Lexus"
                                }
                                dropdownItems={versions}
                                selectChangeHandler={selectChangeHandler}
                                label={"Versión"}
                            />
                        </InputContainer>

                        {/* Precio */}
                        <InputContainer
                            title={"Precio"}
                            extraClass="price-and-discount"
                        >
                            {/* Price box */}
                            <BoxWithBorder
                                type={"price"}
                                title={"Precio"}
                                label={"Precio online"}
                                currency={currency}
                                price={itemToAddToCart.price}
                            />

                            {/* OPTIONAL: Discount */}
                            {discount && <Discount text={discount} />}
                        </InputContainer>

                        {/* Cantidad */}
                        <InputContainer
                            title={"Cantidad de unidades"}
                            extraClass="counter"
                        >
                            <Counter
                                onAdd={onAddHandler}
                                onDecrease={onDecreaseHandler}
                                initial={itemToAddToCart.quantity}
                            />
                        </InputContainer>

                        {/* Instalacion */}
                        {itemToAddToCart.installationCost && (
                            <InputContainer
                                title={"Instalacion"}
                                extraClass="installation"
                            >
                                {/* Toggle box */}
                                <InputCheckBox
                                    type={"toggle-box"}
                                    label={"Agregar instalación"}
                                    id={"add-installation"}
                                    name={"add-installation"}
                                    value={"add-installation"}
                                    changed={installationChangeHandler}
                                    disabled={!itemToAddToCart.price}
                                />

                                {/* Installation cost */}
                                <BoxWithBorder
                                    type={"price"}
                                    label={"Coste de la instalación"}
                                    currency={currency}
                                    price={itemToAddToCart.installationCost}
                                />
                            </InputContainer>
                        )}

                        {/* Agregar al carrito */}
                        <InputContainer
                            title={""}
                            subtitle={""}
                            extraClass="add-to-cart"
                        >
                            <Button
                                tag={"cart-with-total"}
                                text={"Agregar al carrito"}
                                onClick={() =>
                                    addToCartHandler(itemToAddToCart)
                                }
                                currency={currency}
                                totalPrice={
                                    totalPrice && formatPrice(totalPrice)
                                }
                                hasIcon={true}
                                disabled={!itemToAddToCart.price}
                            />
                        </InputContainer>
                        {hasDiscountedPrice && (
                            <DiscountDetail
                                originalPrice={item.price}
                                discount={item.discount_amount}
                            />
                        )}
                    </div>
                </div>
            </div>

            <LegalInformation texts={legalInfo} />
        </section>
    );
};

export default ItemDetailAccesorio;
